import {Button} from '@wandb/components';
import {WBButton} from '@wandb/weave/common/components/elements/WBButtonNew';
import {Link} from '@wandb/weave/common/util/links';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC, useCallback, useContext, useState} from 'react';
import Helmet from 'react-helmet';
import {useParams} from 'react-router-dom';

import Egg from '../../components/Egg';
import NoMatch from '../../components/NoMatch';
import {PageWithSidebar} from '../../components/PageWithSidebar';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ProfileSidebar} from '../../components/ProfileSidebar';
import ProjectsTableUser, {
  ProjectsTableHeaderNew,
} from '../../components/ProjectsTableUser';
import {RoutedTab} from '../../components/RoutedTabNew';
import {AccountSelectorContext} from '../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../components/Search/SearchNav/types';
import {SectionTopHeader} from '../../components/Section/SectionNew';
import {TeamSettings} from '../../components/TeamSettings';
import {UserAccountType} from '../../generated/graphql';
import {Project} from '../../graphql/projects';
import {useQuery} from '../../state/graphql/query';
import {useViewer} from '../../state/viewer/hooks';
import {Graph, Team, UserInfo} from '../../types/graphql';
import {useAdminModeActive} from '../../util/admin';
import {setDocumentTitle} from '../../util/document';
import {isPublicRead} from '../../util/projectAccess';
import {
  useRampFlagAccountSelector,
  useRampFlagStarredProjects,
} from '../../util/rampFeatureFlags';
import {useReportsByAccount} from '../../util/reportsByAccount';
import {StarredProjectsContext} from '../../util/StarredProjectsContextProvider';
import {
  canonicalize,
  profilePage,
  reportGallery,
  setMetaDescription,
} from '../../util/urls';
import {
  CustomizeShowcasedReportsModalUser,
  EntityReport,
  useEntityPageContext,
} from '../EntityPage';
import {EmptyState} from '../EntityPage/EmptyState';
import ReportsTable, {ReportsTableReport} from '../EntityPage/ReportsTable';
import {ProjectCards} from '../HomePage/HomePageContent/ProjectCards';
import {EmptyStarredProjectsCard} from '../HomePage/HomePageContent/ProjectSection';
import {getHomePageProject} from '../HomePage/HomePageContent/util';
import {useAccountTeamsList} from '../HomePage/HomePageSidebar/useAccountTeamsList';
import {MimicTable} from '../Team/MimicTable';
import {PROFILE_PAGE_QUERY} from './../../graphql/profilePage';
import * as S from './ProfilePage.styles';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ProfileOverview} from './ProfilePageOverview';

type ProfilePageProps = {
  filePath: string;
  tab?: string;
};

type ProfilePageQueryVariables = {
  username: string;
};

export type ProfilePageQueryData = {
  user: {
    username: string;
    photoUrl: string;
    name?: string;
    userInfo?: UserInfo;
    hideTeamsFromPublic: boolean;
    id: string;
    accountType: string;
    projects: Graph<Project>;
    views: Graph<ReportsTableReport>;
    starredViews: Graph<ReportsTableReport>;
    teams: Graph<Team>;
  };
};

const ProfilePage: FC<ProfilePageProps> = React.memo(props => {
  const {entityName} = useParams<{entityName: string}>();
  const username = entityName;

  const isAdmin = useAdminModeActive();

  const profilePageQuery = useQuery<
    ProfilePageQueryData,
    ProfilePageQueryVariables
  >(PROFILE_PAGE_QUERY, {
    variables: {username},
  });

  const viewer = useViewer();
  const isCurrentViewerProfilePage =
    viewer?.username != null && entityName === viewer.username;
  const showReportsAndProjectsTab = isAdmin || isCurrentViewerProfilePage;
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const {accountTeams} = useAccountTeamsList();
  const defaultTeam = enableAccountSelector
    ? accountTeams?.edges?.[0]?.node?.name
    : username;

  const {accountReports, isAccountReportsLoading} = useReportsByAccount(
    selectedAccount,
    viewer?.id ?? ''
  );

  const {reportSpec, readOnly, isOwnPersonalEntity} = useEntityPageContext();

  // Entity report edit modal

  const [editingEntityReport, setEditingEntityReport] = useState(false);

  const onEditEntityReport = useCallback(() => {
    setEditingEntityReport(true);
  }, []);

  const onCancelEditEntityReport = useCallback(() => {
    setEditingEntityReport(false);
  }, []);

  const [
    customizeShowcasedReportsModalOpen,
    setCustomizeShowcasedReportsModalOpen,
  ] = useState(false);

  const openCustomizeShowcasedReportsModal = useCallback(() => {
    setCustomizeShowcasedReportsModalOpen(true);
  }, []);

  const closeCustomizeShowcasedReportsModal = useCallback(() => {
    setCustomizeShowcasedReportsModalOpen(false);
  }, []);

  const data = profilePageQuery.loading ? null : profilePageQuery.data?.user;

  const isStarredProjectsEnabled = useRampFlagStarredProjects();
  const {starredProjects: starredProjectsMap} = React.useContext(
    StarredProjectsContext
  );
  const starredProjects = Object.values(starredProjectsMap)
    .map(p => getHomePageProject(p))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  React.useEffect(() => {
    // meta description tag for google search results
    if (data) {
      if (username) {
        setDocumentTitle(getTitle(data.name ?? data.username), false);
      }
      const {name, userInfo} = data;
      const descParts: string[] = [];
      if (name) {
        descParts.push(`View ML projects from ${name} on Weights & Biases.`);
      }
      if (userInfo != null) {
        const {bio, company, location} = userInfo;
        if (bio) {
          descParts.push(`${bio}.`);
        }
        if (company && location) {
          descParts.push(`Working at ${company} in ${location}.`);
        } else if (company) {
          descParts.push(`Working at ${company}.`);
        } else if (location) {
          descParts.push(`Working in ${location}.`);
        }
      }
      if (descParts.length > 0) {
        setMetaDescription(descParts.join(' '));
      }
    }
  }, [data, username]);

  if (data?.accountType === UserAccountType.Anonymous) {
    return <NoMatch />;
  }

  const showLikesTab =
    (data && data.starredViews?.edges?.length > 0) || isOwnPersonalEntity;

  const reports =
    enableAccountSelector && isCurrentViewerProfilePage
      ? accountReports
      : data?.views;
  const userHasPublicReports =
    reports?.edges?.some(({node: r}) => isPublicRead(r.project)) ?? false;

  return (
    <PageWithSidebar
      sidebar={
        <>
          <Helmet>
            <meta property="og:type" content="profile" />
            <meta
              property="og:url"
              content={canonicalize(window.location.href)}
            />
            <meta
              property="og:title"
              content={getTitle(data?.name ?? username)}
            />
            <meta property="og:image" content={data?.photoUrl} />
            {data?.userInfo?.bio && (
              <meta property="og:description" content={data?.userInfo.bio} />
            )}
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:creator" content={username} />
            <link rel="canonical" href={canonicalize(window.location.href)} />
          </Helmet>
          {data && <ProfileSidebar data={data} isEditable={!readOnly} />}
        </>
      }>
      <>
        <S.ProfilePageContent>
          <RoutedTab
            data-test="profile-page-tabs"
            baseUrl={profilePage(username)}
            tabSlugs={[
              'overview',
              ...(showReportsAndProjectsTab ? ['reports'] : []),
              ...(showReportsAndProjectsTab ? ['projects'] : []),
              ...(showLikesTab ? ['stars'] : []),
              ...(isAdmin ? ['settings'] : []),
            ]}
            activeTabSlug={props.tab}
            panes={[
              {
                menuItem: 'Profile',
                render: () => (
                  <>
                    {(!readOnly || reportSpec != null) && (
                      <>
                        <SectionTopHeader>
                          <div>Intro</div>
                          <Button variant="ghost" onPress={onEditEntityReport}>
                            Edit
                          </Button>
                        </SectionTopHeader>
                        <EntityReport
                          data-test={`entity-report-section`}
                          editing={editingEntityReport}
                          onEdit={onEditEntityReport}
                          onCancelEdit={onCancelEditEntityReport}
                        />
                      </>
                    )}
                    {data && (
                      <ProfileOverview
                        data={data}
                        openCustomizeShowcasedReportsModal={
                          openCustomizeShowcasedReportsModal
                        }
                        closeCustomizeShowcasedReportsModal={
                          closeCustomizeShowcasedReportsModal
                        }
                        customizeShowcasedReportsModalOpen={
                          customizeShowcasedReportsModalOpen
                        }
                        userHasPublicReports={userHasPublicReports}
                      />
                    )}
                  </>
                ),
              },
              ...(showReportsAndProjectsTab
                ? [
                    {
                      menuItem: 'Reports',
                      render: () => (
                        <>
                          {isAccountReportsLoading ? (
                            <MimicTable
                              columnNames={[
                                'Title',
                                'Showcase',
                                'Project',
                                'Author',
                                'Last Edit',
                              ]}
                              columnSizes={'5fr 3fr 3.2fr 3fr 3fr'}
                              numRows={10}
                            />
                          ) : !userHasPublicReports &&
                            !isCurrentViewerProfilePage ? (
                            <EmptyState
                              iconName="lock-closed"
                              header={`This account has no public reports`}
                              withExtraPadding
                            />
                          ) : (
                            <SectionTopHeader>
                              <div>
                                {isCurrentViewerProfilePage &&
                                enableAccountSelector
                                  ? `Your ${
                                      selectedAccount?.accountType ===
                                      AccountType.Personal
                                        ? 'personal'
                                        : selectedAccount?.name
                                    } reports`
                                  : 'Reports'}
                              </div>
                              {!readOnly && userHasPublicReports && (
                                <WBButton
                                  onClick={openCustomizeShowcasedReportsModal}>
                                  Select reports to showcase
                                </WBButton>
                              )}
                            </SectionTopHeader>
                          )}
                          <ReportsTable
                            data={reports ?? null}
                            pageSize={10}
                            showShowcase={!readOnly}
                            isTeamPage={false}
                            suppressHeader
                          />
                          {customizeShowcasedReportsModalOpen && (
                            <CustomizeShowcasedReportsModalUser
                              onClose={closeCustomizeShowcasedReportsModal}
                            />
                          )}
                        </>
                      ),
                    },
                  ]
                : []),
              ...(showReportsAndProjectsTab
                ? [
                    {
                      menuItem: 'Projects',
                      render: () => (
                        <Tailwind>
                          {isStarredProjectsEnabled &&
                            isCurrentViewerProfilePage && (
                              <>
                                <SectionTopHeader className="pb-4">
                                  Your starred projects
                                </SectionTopHeader>
                                {starredProjects?.length > 0 ? (
                                  <ProjectCards projects={starredProjects} />
                                ) : (
                                  <EmptyStarredProjectsCard />
                                )}
                              </>
                            )}
                          <ProjectsTableHeaderNew
                            entityName={defaultTeam}
                            readOnly={readOnly}
                            isCurrentViewerProfilePage={
                              isCurrentViewerProfilePage
                            }
                            enableAccountSelector={enableAccountSelector}
                            selectedAccount={selectedAccount}
                          />
                          <ProjectsTableUser
                            entityName={username}
                            readOnly={readOnly}
                            suppressHeader
                          />
                        </Tailwind>
                      ),
                    },
                  ]
                : []),
              ...(showLikesTab
                ? [
                    {
                      menuItem: 'Stars',
                      render: () =>
                        data && data.starredViews.edges.length > 0 ? (
                          <ReportsTable
                            data={data?.starredViews ?? null}
                            pageSize={20}
                            isTeamPage={false}
                          />
                        ) : (
                          <div className="no-stars-watermark">
                            You haven’t starred any reports yet. Check out{' '}
                            <Link to={reportGallery()}>Fully Connected</Link>{' '}
                            for curated reports on popular machine learning
                            research topics.
                          </div>
                        ),
                    },
                  ]
                : []),
              ...(isAdmin
                ? [
                    {
                      menuItem: 'Settings',
                      render: () => <TeamSettings entityName={username} />,
                    },
                  ]
                : []),
            ]}
          />
        </S.ProfilePageContent>
        <Egg />
      </>
    </PageWithSidebar>
  );
});
ProfilePage.displayName = 'ProfilePage';

export default ProfilePage;

function getTitle(user: string): string {
  return `${user} - Machine Learning Portfolio in Weights & Biases`;
}
